/**
 * Shared display labels for transactions (types and statuses), so the admin Transactions page, the
 * member profile page and the member Billing page all describe a transaction the same way.
 */

export const TRANSACTION_TYPE_LABELS: Record<string, string> = {
    membership_payment: 'Membership Payment',
    amenity_booking: 'Amenity Booking',
    class_booking: 'Class Booking',
    initiation_fee: 'Initiation Fee',
    guest_fee: 'Guest Fee',
    improvement_fee: 'Improvement Fee',
    coaching: 'Coaching',
    personal_training: 'Personal Training',
    personal_training_pack: 'Personal Training Pack',
    equipment_fee: 'Equipment Fee',
    add_on_payment: 'Add-On Payment',
    group_play_booking: 'Group Play Booking',
    refund: 'Refund',
    wallet_credit: 'Wallet Credit',
    pos_purchase: 'Shop Purchase',
    fnb_purchase: 'F&B Purchase',
    no_show_fee: 'No-Show Fee',
    other: 'Other',
};

/** "pos_purchase" → "Shop Purchase"; unknown types fall back to Title Case */
export function formatTransactionType(type: string | undefined | null): string {
    if (!type) return '';
    return TRANSACTION_TYPE_LABELS[type]
        ?? type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}

export type ChipColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

export function getTransactionStatusColor(status: string | undefined | null): ChipColor {
    switch ((status ?? '').toLowerCase()) {
        case 'completed':
        case 'manually_settled':
            return 'success';
        case 'pending':
        case 'in_progress':
            return 'warning';
        case 'failed':
        case 'written_off':
            return 'error';
        case 'refunded':
            return 'info';
        case 'cancelled':
        default:
            return 'default';
    }
}

/** Member-facing status label (manually_settled reads as "Paid", not "Cash Paid") */
export function getTransactionStatusLabel(status: string | undefined | null): string {
    switch (status) {
        case 'manually_settled': return 'Paid';
        case 'in_progress': return 'Processing';
        case 'written_off': return 'Written Off';
        default: return (status ?? '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    }
}

/** True for Shop / F&B sales charged to a member account */
export function isPosSaleTransaction(type: string | undefined | null): boolean {
    return type === 'pos_purchase' || type === 'fnb_purchase';
}
